import { useState, useEffect, useMemo } from 'react';
import { Link, Navigate } from 'react-router';
import { Plus, TrendingUp, CheckCircle, Clock, AlertCircle, Star, History, Bell } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, PieChart, Pie, Cell
} from 'recharts';
import { addStandupNote } from '../services/dataService';
import { updateTask } from '../store';
import type { ChangeLog, StandupNote, TimeLog } from '../types';
import { generateAutomaticNotifications } from '../utils/notificationChecker';
import { getProjectStatusClassName, isProjectInProgress, normalizeProjectStatus } from '../utils/projectStatus';
import { useApp } from '../providers/AppProvider';
import { useOrganizationData } from '../hooks/useOrganizationData';
import { parseDateLike } from '../utils/dateUtils';
import QuickLogger from '../components/QuickLogger';
import SurfaceCard from '../components/SurfaceCard';
import { getProjectStatusBadgeColors, getStatusColors, getTaskTypeColors } from '../constants/colorScheme';

const toTime = (value: unknown, fallback: number = 0): number => {
  const parsedDate = parseDateLike(value);
  return parsedDate ? parsedDate.getTime() : fallback;
};

const formatDate = (value: unknown, fallback: string = 'Unknown date'): string => {
  const parsedDate = parseDateLike(value);
  return parsedDate ? parsedDate.toLocaleDateString() : fallback;
};

const ACTIVE_PROJECT_STAT_CLASS = `border ${getProjectStatusBadgeColors('In Progress')}`;
const ACTIVE_TASK_STAT_CLASS = `border ${getTaskTypeColors('Improvement').badge}`;
const COMPLETED_STAT_CLASS = `border ${getStatusColors('Completed').badge}`;
const BUG_STAT_CLASS = `border ${getTaskTypeColors('Bug').badge}`;
const NEUTRAL_BADGE_CLASS = `border ${getStatusColors('Not Started').badge}`;

const getSprintTimelineBadgeClass = (daysRemaining: number): string => {
  if (daysRemaining > 7) {
    return `border ${getStatusColors('Completed').badge}`;
  }

  if (daysRemaining > 0) {
    return `border ${getStatusColors('Deferred').badge}`;
  }

  return `border ${getStatusColors('Blocked').badge}`;
};

const getRatingBadgeClass = (rating: number): string => {
  if (rating >= 4) {
    return `border ${getStatusColors('Completed').badge}`;
  }

  if (rating >= 2) {
    return `border ${getStatusColors('Deferred').badge}`;
  }

  return NEUTRAL_BADGE_CLASS;
};

export default function Dashboard() {
  /* ------------------------------------------------------------------ */
  /* STATE                                                              */
  /* ------------------------------------------------------------------ */

  const { state, loading, error, setError, reload } = useApp();
  const [autoNotifications, setAutoNotifications] = useState<any[]>([]);
  

  /* ------------------------------------------------------------------ */
  /* LOAD STATE                                                         */
  /* ------------------------------------------------------------------ */

  useEffect(() => {
    async function init() {
      try {
        if (!state) {
          return;
        }

        const notifications = generateAutomaticNotifications(state);
        setAutoNotifications(notifications);
      } catch (err: any) {
        console.error('[Dashboard] Failed to load:', err);
        setError(err.message || 'Failed to load dashboard');
      }
    }

    init();
    return () => { /* cleanup if needed */ };
  }, [state, loading, setError]);

  // Handle standup and time log submissions
  const handleStandupSubmit = async (note: StandupNote) => {
    try {
      if (state?.currentUser?.organizationId) {
        await addStandupNote(note);
        setError(null);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to submit standup note');
    }
  };

  const handleLogHours = async (taskId: string, hours: number, description: string, date: string) => {
    try {
      if (!state?.currentUser) {
        throw new Error('User context not available');
      }

      const task = state.tasks.find((candidate) => candidate.id === taskId);
      if (!task) {
        throw new Error('Task not found');
      }

      const timeLog: TimeLog = {
        id: `tl-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        taskId,
        userId: state.currentUser.id,
        hours,
        description: description || 'Logged from dashboard quick logger',
        date
      };

      const updatedLogs = [...(task.timeLogs || []), timeLog];
      await updateTask(taskId, {
        timeLogs: updatedLogs,
        updatedAt: new Date().toISOString(),
      });

      if (state.currentUser.email) {
        await reload(state.currentUser.email);
      }

      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to log hours');
    }
  };

  /* ------------------------------------------------------------------ */
  /* SAFE DEFAULTS                                                      */
  /* ------------------------------------------------------------------ */

  const { projects, tasks, sprints, users } = useOrganizationData(state, {
    includeAllOrganizations: true,
  }) as {
    projects: any[];
    tasks: any[];
    sprints: any[];
    users: any[];
  };
  const currentUser = state?.currentUser;

  const myQuickLoggerTasks = useMemo(() => {
    if (!currentUser) return [];

    const normalizeIdentity = (value: unknown): string => {
      return String(value || '').trim().toLowerCase();
    };

    const identities = new Set<string>();

    const registerIdentity = (value: unknown) => {
      const normalized = normalizeIdentity(value);
      if (!normalized) return;

      identities.add(normalized);

      const withoutPrefix = normalized.replace(/^user[-_]/, '');
      if (withoutPrefix) {
        identities.add(withoutPrefix);
      }

      const compact = normalized.replace(/[^a-z0-9]/g, '');
      if (compact) {
        identities.add(compact);
      }

      if (normalized.includes('@')) {
        const localPart = normalized.split('@')[0];
        identities.add(localPart);
        identities.add(localPart.replace(/[^a-z0-9]/g, ''));
      }
    };

    registerIdentity(currentUser.id);
    registerIdentity(currentUser.email);

    return tasks.filter((task) =>
      (task.assigneeIds || []).some((assigneeId: unknown) => {
        const normalized = normalizeIdentity(assigneeId);
        if (!normalized) return false;
        if (identities.has(normalized)) return true;

        const compact = normalized.replace(/[^a-z0-9]/g, '');
        if (compact && identities.has(compact)) return true;

        const withoutPrefix = normalized.replace(/^user[-_]/, '');
        if (withoutPrefix && identities.has(withoutPrefix)) return true;

        if (normalized.includes('@')) {
          const localPart = normalized.split('@')[0];
          return identities.has(localPart) || identities.has(localPart.replace(/[^a-z0-9]/g, ''));
        }

        return false;
      })
    );
  }, [tasks, currentUser]);

  /* ------------------------------------------------------------------ */
  /* MEMOS                                                              */
  /* ------------------------------------------------------------------ */

  const activeProjects = useMemo(
    () => projects.filter(p => isProjectInProgress((p as any).status)),
    [projects]
  );
  const activeTasks = useMemo(() => tasks.filter(t => t.status === 'Active' || t.status === 'In Progress'), [tasks]);
  const completedTasks = useMemo(() => tasks.filter(t => t.status === 'Completed'), [tasks]);
  const openBugs = useMemo(() => tasks.filter(t => t.type === 'Bug' && t.status !== 'Completed'), [tasks]);
  const activeSprints = useMemo(() => sprints.filter(s => s.status === 'Active'), [sprints]);
  const activeTasksProjectId = activeTasks[0]?.projectId || activeProjects[0]?.id;
  const completedTasksProjectId = completedTasks[0]?.projectId || activeProjects[0]?.id;

  const tasksByStatus = useMemo(() => [
    { name: 'Active', value: tasks.filter(t => t.status === 'Active').length, color: '#3b82f6' },
    { name: 'In Progress', value: tasks.filter(t => t.status === 'In Progress').length, color: '#8b5cf6' },
    { name: 'Completed', value: completedTasks.length, color: '#10b981' },
    { name: 'Deferred', value: tasks.filter(t => t.status === 'Deferred').length, color: '#f59e0b' },
    { name: 'Blocked', value: tasks.filter(t => t.status === 'Blocked').length, color: '#ef4444' },
  ], [tasks, completedTasks.length]);

  const tasksByType = useMemo(() => [
    { name: 'Features', count: tasks.filter(t => t.type === 'Feature').length },
    { name: 'Bugs', count: tasks.filter(t => t.type === 'Bug').length },
    { name: 'Improvements', count: tasks.filter(t => t.type === 'Improvement').length },
  ], [tasks]);

  const topRatedTasks = useMemo(() => {
    return tasks
      .filter(t => t.rating && t.rating > 0)
      .sort((a, b) => (b.rating || 0) - (a.rating || 0))
      .slice(0, 5);
  }, [tasks]);

  const developerStats = useMemo(() => {
    return users
      .filter(u => u.role === 'developer')
      .map(user => {
        const userTasks = tasks.filter(t => t.assigneeIds.includes(user.id));
        const completedCount = userTasks.filter(t => t.status === 'Completed').length;

        const totalHours = userTasks.reduce((sum, task) => {
          return sum + task.timeLogs
            .filter((log: any) => log.userId === user.id)
            .reduce((s: number, log: any) => s + log.hours, 0);
        }, 0);

        const ratedTasks = userTasks.filter(t => t.rating);
        const avgRating = ratedTasks.length
          ? ratedTasks.reduce((s, t) => s + (t.rating || 0), 0) / ratedTasks.length
          : 0;

        return {
          ...user,
          completedTasksCount: completedCount,
          totalHours,
          avgTaskRating: avgRating,
        };
      })
      .sort((a, b) => b.completedTasksCount - a.completedTasksCount);
  }, [users, tasks]);

  const latestChanges = useMemo(() => {
    const changes: Array<{ task: string; change: ChangeLog; taskObj: any }> = [];

    tasks.forEach(task => {
      task.changeLogs?.forEach((log: ChangeLog) => {
        if (log.changeType === 'assignee' || log.changeType === 'targetDate') {
          changes.push({ task: task.title, change: log, taskObj: task });
        }
      });
    });

    return changes
      .sort((a, b) =>
        toTime(b.change.changedAt) -
        toTime(a.change.changedAt)
      )
      .slice(0, 10);
  }, [tasks]);

  /* ------------------------------------------------------------------ */
  /* EARLY RETURNS                                                      */
  /* ------------------------------------------------------------------ */

  if (loading) return <div className="p-6 text-muted-foreground">Loading dashboard…</div>;
  if (error || !state) return <div className="p-6 text-destructive">{error || 'Failed to load dashboard'}</div>;
  if (currentUser?.role === 'external_partner') {
    return <Navigate to="/dashboard" replace />;
  }

  /* ------------------------------------------------------------------ */
  /* STATS                                                              */
  /* ------------------------------------------------------------------ */

  const stats = [
    {
      name: 'Active Projects',
      value: activeProjects.length,
      icon: TrendingUp,
      toneClass: ACTIVE_PROJECT_STAT_CLASS,
      link: '/app/projects?status=In%20Progress',
    },
    {
      name: 'Active Tasks',
      value: activeTasks.length,
      icon: Clock,
      toneClass: ACTIVE_TASK_STAT_CLASS,
      link: activeTasksProjectId
        ? `/app/projects/${activeTasksProjectId}/spreadsheet?status=Open`
        : '/app/projects',
    },
    {
      name: 'Completed Tasks',
      value: completedTasks.length,
      icon: CheckCircle,
      toneClass: COMPLETED_STAT_CLASS,
      link: completedTasksProjectId
        ? `/app/projects/${completedTasksProjectId}/spreadsheet?status=Completed`
        : '/app/projects',
    },
    {
      name: 'Open Bugs',
      value: openBugs.length,
      icon: AlertCircle,
      toneClass: BUG_STAT_CLASS,
      link: '/app/bugs',
    },
  ];

  /* ------------------------------------------------------------------ */
  /* UI (UNCHANGED)                                                     */
  /* ------------------------------------------------------------------ */

  return (
    <div className="px-4 sm:px-6 lg:px-8 pt-3 lg:pt-4 pb-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 sm:mb-5 gap-3">
        <div>
          <h1 className="mb-1 text-xl font-semibold text-foreground sm:text-2xl">Dashboard</h1>
          <p className="text-sm text-muted-foreground">
            Welcome back, {currentUser?.name ?? 'User'}
          </p>
        </div>
        <Link
          to="/app/projects"
          className="inline-flex h-9 items-center justify-center gap-2 rounded-lg bg-primary px-3 text-sm text-primary-foreground transition-colors hover:bg-primary/90"
        >
          <Plus className="w-4 h-4" />
          <span className="hidden sm:inline">New Project</span>
          <span className="sm:hidden">New</span>
        </Link>
      </div>

      {/* Quick Logger - Compact entry point for standup and time logging */}
      <div className="mb-6">
        {currentUser && (
          <QuickLogger
            currentUser={currentUser}
            assignedTasks={myQuickLoggerTasks}
            onStandupSubmit={handleStandupSubmit}
            onLogHours={handleLogHours}
          />
        )}
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {stats.map((stat) => {
          const Icon = stat.icon;
          return (
            <Link
              key={stat.name}
              to={stat.link}
              className="rounded-lg border border-border bg-card p-4 shadow-sm transition-shadow hover:shadow-md"
            >
              <div className="flex items-center justify-between mb-3">
                <div className={`${stat.toneClass} rounded-lg p-2`}>
                  <Icon className="h-5 w-5" />
                </div>
                <span className="text-2xl font-semibold text-foreground">{stat.value}</span>
              </div>
              <h3 className="text-sm text-muted-foreground">{stat.name}</h3>
            </Link>
          );
        })}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Tasks by Type */}
        <SurfaceCard className="p-6">
          <h2 className="mb-4 text-xl text-foreground">Tasks by Type</h2>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={tasksByType}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="count" fill="#3b82f6" />
            </BarChart>
          </ResponsiveContainer>
        </SurfaceCard>

        {/* Tasks by Status */}
        <SurfaceCard className="p-6">
          <h2 className="mb-4 text-xl text-foreground">Tasks by Status</h2>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={tasksByStatus}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, value }) => value > 0 ? `${name}: ${value}` : ''}
                outerRadius={100}
                fill="#8884d8"
                dataKey="value"
              >
                {tasksByStatus.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </SurfaceCard>
      </div>

      {/* Recent Activity */}
      <SurfaceCard className="p-6">
        <h2 className="mb-4 text-xl text-foreground">Recent Projects</h2>
        {projects.length === 0 ? (
          <div className="text-center py-12">
            <p className="mb-4 text-muted-foreground">No projects yet</p>
            <Link
              to="/app/projects"
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-primary-foreground hover:bg-primary/90"
            >
              <Plus className="w-4 h-4" />
              Create Your First Project
            </Link>
          </div>
        ) : (
          <div className="space-y-3">
            {projects.slice(0, 5).map((project) => (
              <Link
                key={project.id}
                to={`/app/projects/${project.id}`}
                className="flex items-center justify-between rounded-lg border border-border p-4 transition-colors hover:bg-accent"
              >
                <div>
                  <h3 className="mb-1 text-foreground">{project.name}</h3>
                  <p className="text-sm text-muted-foreground">{project.description}</p>
                </div>
                <span
                  className={`inline-flex rounded-full px-3 py-1 text-sm ${getProjectStatusClassName(project.status)}`}
                >
                  {normalizeProjectStatus(project.status) || project.status}
                </span>
              </Link>
            ))}
          </div>
        )}
      </SurfaceCard>

      {/* Active Sprints */}
      <SurfaceCard className="mt-8 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl text-foreground">Active Sprints</h2>
          <Link
            to="/app/projects"
            className="text-sm text-primary hover:text-primary/80"
          >
            View All
          </Link>
        </div>
        {activeSprints.length === 0 ? (
          <div className="text-center py-12">
            <p className="mb-4 text-muted-foreground">No active sprints</p>
          </div>
        ) : (
          <div className="space-y-4">
            {activeSprints.map((sprint) => {
              const sprintTasks = tasks.filter(t => (sprint.taskIds || []).includes(t.id));
              const completedSprintTasks = sprintTasks.filter(t => t.status === 'Completed');
              const inProgressSprintTasks = sprintTasks.filter(t => t.status === 'In Progress' || t.status === 'Active');
              const completionPercentage = sprintTasks.length > 0
                ? Math.round((completedSprintTasks.length / sprintTasks.length) * 100)
                : 0;
              
              const startDate = parseDateLike(sprint.startDate);
              const endDate = parseDateLike(sprint.endDate);
              const daysRemaining = endDate
                ? Math.ceil((endDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24))
                : 0;
              
              const project = projects.find(p => p.id === sprint.projectId);

              return (
                <Link
                  key={sprint.id}
                  to={`/app/projects/${sprint.projectId}/sprints/${sprint.id}`}
                  className="block rounded-lg border border-border p-4 transition-colors hover:bg-accent"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="mb-1 font-medium text-foreground">{sprint.name}</h3>
                      <p className="text-sm text-muted-foreground">{project?.name || 'Unknown Project'}</p>
                    </div>
                    <span
                      className={`inline-flex rounded-full px-3 py-1 text-sm ${getSprintTimelineBadgeClass(daysRemaining)}`}
                    >
                      {daysRemaining > 0 ? `${daysRemaining} days left` : 'Overdue'}
                    </span>
                  </div>
                  <div className="mb-3 flex items-center gap-4 text-sm text-muted-foreground">
                    <span>{startDate ? startDate.toLocaleDateString() : 'Unknown date'} - {endDate ? endDate.toLocaleDateString() : 'Unknown date'}</span>
                    <span>•</span>
                    <span>{sprintTasks.length} tasks</span>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Progress</span>
                      <span className="font-medium text-foreground">{completionPercentage}%</span>
                    </div>
                    <progress
                      value={Math.max(0, Math.min(completionPercentage, 100))}
                      max={100}
                      className="h-2 w-full overflow-hidden rounded-full [&::-webkit-progress-bar]:bg-muted [&::-webkit-progress-value]:bg-primary [&::-moz-progress-bar]:bg-primary"
                    />
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      <span>{completedSprintTasks.length} completed</span>
                      <span>•</span>
                      <span>{inProgressSprintTasks.length} in progress</span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </SurfaceCard>

      {/* Recent Changes */}
      <SurfaceCard className="mt-8 p-6">
        <h2 className="mb-4 flex items-center gap-2 text-xl text-foreground">
          <History className="w-5 h-5" />
          Recent Changes
        </h2>
        {latestChanges.length === 0 ? (
          <div className="text-center py-12">
            <p className="mb-4 text-muted-foreground">No recent changes</p>
          </div>
        ) : (
          <div className="space-y-3">
            {latestChanges.map((item, index) => {
              const changedBy = users.find(u => u.id === item.change.changedBy);
              return (
                <div
                  key={`${item.change.id}-${index}`}
                  className="rounded-lg border border-border p-4 transition-colors hover:bg-accent"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <h3 className="mb-1 text-foreground">{item.task}</h3>
                      <p className="text-sm text-muted-foreground">
                        <span className="capitalize">{item.change.changeType}</span> changed by {changedBy?.name || 'Unknown'}
                      </p>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {formatDate(item.change.changedAt)}
                    </span>
                  </div>
                  <div className="rounded bg-muted/40 p-2 text-sm text-foreground">
                    <span className="text-muted-foreground">From:</span> {item.change.oldValue} 
                    <span className="mx-2">→</span>
                    <span className="text-muted-foreground">To:</span> {item.change.newValue}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </SurfaceCard>

      {/* Top Rated Tasks */}
      <SurfaceCard className="mt-8 p-6">
        <h2 className="mb-4 flex items-center gap-2 text-xl text-foreground">
          <Star className="w-5 h-5" />
          Top Rated Tasks
        </h2>
        {topRatedTasks.length === 0 ? (
          <div className="text-center py-12">
            <p className="mb-4 text-muted-foreground">No rated tasks</p>
          </div>
        ) : (
          <div className="space-y-3">
            {topRatedTasks.map((task) => (
              <Link
                key={task.id}
                to={`/task/${task.id}`}
                className="flex items-center justify-between rounded-lg border border-border p-4 transition-colors hover:bg-accent"
              >
                <div>
                  <h3 className="mb-1 text-foreground">{task.title}</h3>
                  <p className="text-sm text-muted-foreground">{task.description}</p>
                </div>
                <span
                  className={`inline-flex rounded-full px-3 py-1 text-sm ${getRatingBadgeClass(task.rating || 0)}`}
                >
                  {task.rating} ★
                </span>
              </Link>
            ))}
          </div>
        )}
      </SurfaceCard>

      {/* Developer Performance */}
      <SurfaceCard className="p-6">
        <h2 className="mb-4 text-xl text-foreground">Developer Performance</h2>
        {developerStats.length === 0 ? (
          <div className="text-center py-12">
            <p className="mb-4 text-muted-foreground">No developers</p>
          </div>
        ) : (
          <div className="space-y-3">
            {developerStats.map((developer) => (
              <div
                key={developer.id}
                className="flex items-center justify-between rounded-lg border border-border p-4 transition-colors hover:bg-accent"
              >
                <div>
                  <h3 className="mb-1 text-foreground">{developer.name}</h3>
                  <p className="text-sm text-muted-foreground">
                    {developer.completedTasksCount} completed tasks, {developer.totalHours} hours worked
                  </p>
                </div>
                <span
                  className={`inline-flex rounded-full px-3 py-1 text-sm ${getRatingBadgeClass(developer.avgTaskRating)}`}
                >
                  {developer.avgTaskRating.toFixed(1)} ★
                </span>
              </div>
            ))}
          </div>
        )}
      </SurfaceCard>

      {/* Notifications */}
      <SurfaceCard className="mt-8 p-6">
        <h2 className="mb-4 flex items-center gap-2 text-xl text-foreground">
          <Bell className="w-5 h-5" />
          Notifications
        </h2>
        {autoNotifications.length === 0 ? (
          <div className="text-center py-12">
            <p className="mb-4 text-muted-foreground">No notifications</p>
          </div>
        ) : (
          <div className="space-y-3">
            {autoNotifications.map((notification, index) => (
              <div
                key={`notification-${index}`}
                className="rounded-lg border border-border p-4 transition-colors hover:bg-accent"
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <h3 className="mb-1 text-foreground">{notification.title}</h3>
                    <p className="text-sm text-muted-foreground">
                      {notification.message}
                    </p>
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {formatDate(notification.createdAt || notification.timestamp || Date.now())}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </SurfaceCard>

    </div>
  );
}